import {
  computeItemCharge,
  occurrencesInRange,
  rentalDays,
  weekdayLabels,
  type RentalItem,
  type RentalOrder,
} from './model';

/**
 * Die Abrechnungsberechnung des Mietparks -- die EINE Quelle der Wahrheit.
 *
 * Bewusst hier und nicht im Gateway: Demo-Gateway UND Supabase-Adapter rechnen die Zeilen mit
 * genau diesen Funktionen. Der Supabase-RPC speichert nur den Zustand (billed_through,
 * billing_status); die Zeilen entstehen hier. Zwei Rechenwege waeren zwei Stellen, die man
 * korrekt halten muesste -- und Abrechnung ist der Ort, an dem "fast richtig" geschaeftsschaedigend ist.
 */

export interface RentalBillingLine {
  readonly kind: 'service' | 'freight' | 'other';
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  readonly vatRate: number;
  /** Maschine dieser Zeile (Nummer) — Bezug für die Warengruppen-Auflösung beim Abrechnen (Stufe 3). */
  readonly machineNumber?: string;
}

export interface RentalBilling {
  readonly rentalNumber: string;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly periodLabel: string;
  /** Leistungszeitraum als ISO-Datum -- speist Leistungsdatum/-zeitraum der Rechnung (§ 14 UStG). */
  readonly periodFrom: string | null;
  readonly periodTo: string | null;
  readonly lines: readonly RentalBillingLine[];
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
function minDate(a: string, b: string): string {
  return a < b ? a : b;
}
function shortDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
}

/** Rechnungszeilen fuer [from, to] -- je Position eine Zeile (Standard) bzw. einsatzbasiert (Intervall). */
export function billingLinesFor(order: RentalOrder, from: string, to: string): RentalBillingLine[] {
  if (order.kind === 'interval' && order.intervalWeekday !== null) {
    const weekday = order.intervalWeekday;
    const occurrences = occurrencesInRange(order, from, to);
    if (occurrences.length === 0) return [];
    const lines: RentalBillingLine[] = order.items.map((item) => ({
      kind: 'service',
      description: `Intervallmiete ${item.machineLabel} (${occurrences.length}× ${weekdayLabels[weekday]})`,
      quantity: occurrences.length,
      unit: 'Einsatz',
      unitPrice: item.dailyRate,
      vatRate: 19,
      machineNumber: item.machineNumber,
    }));
    if (order.transportCost > 0) {
      lines.push({
        kind: 'freight',
        description: 'Transporte (Zustellung + Abholung je Einsatz)',
        quantity: occurrences.length * 2 * order.items.length,
        unit: 'Fahrt',
        unitPrice: order.transportCost,
        vatRate: 19,
      });
    }
    return lines;
  }
  return order.items.map((item) => rentalLine(item, from, to));
}

function rentalLine(item: RentalItem, from: string, to: string): RentalBillingLine {
  return {
    kind: 'service',
    description: `Miete ${item.machineLabel} (${shortDate(from)} – ${shortDate(to)})`,
    quantity: rentalDays(from, to),
    unit: 'Tag',
    unitPrice: round2(computeItemCharge(item, from, to) / rentalDays(from, to)),
    vatRate: 19,
    machineNumber: item.machineNumber,
  };
}

/**
 * Monatsabrechnung: der naechste offene 30-Tage-Abschnitt ab billedThrough (bzw. Mietbeginn).
 * Wirft dieselben fachlichen Fehler wie bisher -- nichts mehr abzurechnen, kein Einsatz im Zeitraum.
 * Gibt zusaetzlich `to` zurueck: das neue billedThrough, das der Aufrufer persistiert.
 */
export function computeMonthlyBilling(order: RentalOrder): { readonly to: string; readonly billing: RentalBilling } {
  if (order.billingStatus === 'billed') {
    throw new Error('Der Mietauftrag ist bereits vollständig abgerechnet.');
  }
  const from = order.billedThrough ? addDays(order.billedThrough, 1) : order.startDate;
  if (from > order.endDate) {
    throw new Error('Es ist kein weiterer Zeitraum abzurechnen – bitte Schlussabrechnung nutzen.');
  }
  const to = minDate(addDays(from, 29), order.endDate);
  const lines = billingLinesFor(order, from, to);
  if (lines.length === 0) {
    throw new Error('In diesem Zeitraum gibt es keine abzurechnenden Einsätze.');
  }
  return {
    to,
    billing: {
      rentalNumber: order.number,
      customerNumber: order.customerNumber,
      customerName: order.customerName,
      periodLabel: `${shortDate(from)} – ${shortDate(to)}`,
      periodFrom: from,
      periodTo: to,
      lines,
    },
  };
}

/** Schlussabrechnung: Restzeitraum + Schaeden + Kaution-Verrechnung. */
export function computeFinalBilling(order: RentalOrder): RentalBilling {
  if (order.billingStatus === 'billed') {
    throw new Error('Der Mietauftrag ist bereits abgerechnet.');
  }
  const to = order.items.reduce<string | null>(
    (latest, item) => item.actualReturnDate && (!latest || item.actualReturnDate > latest) ? item.actualReturnDate : latest,
    null,
  ) ?? order.endDate;
  const from = order.billedThrough ? addDays(order.billedThrough, 1) : order.startDate;
  const lines: RentalBillingLine[] = [];
  if (from <= to) lines.push(...billingLinesFor(order, from, to));
  order.items.forEach((item) => {
    if (item.damageAmount > 0) {
      lines.push({ kind: 'other', description: `Schaden ${item.machineLabel}${item.damageNote ? ` – ${item.damageNote}` : ''}`, quantity: 1, unit: 'Pauschale', unitPrice: item.damageAmount, vatRate: 19, machineNumber: item.machineNumber });
    }
  });
  // Transport bei Standardmiete: einmalige Pauschale (An-/Abfahrt) in der Schlussabrechnung.
  // Intervallmiete rechnet Transport dagegen je Einsatz -- das steckt schon in billingLinesFor.
  if (order.kind !== 'interval' && order.transportCost > 0) {
    lines.push({ kind: 'freight', description: 'Transport (An- und Abfahrt)', quantity: 1, unit: 'Pauschale', unitPrice: order.transportCost, vatRate: 19 });
  }
  if (order.depositAmount > 0) {
    lines.push({ kind: 'other', description: 'Kaution-Verrechnung', quantity: 1, unit: 'Pauschale', unitPrice: -order.depositAmount, vatRate: 0 });
  }
  if (lines.length === 0) {
    lines.push({ kind: 'other', description: 'Schlussabrechnung Miete', quantity: 1, unit: 'Pauschale', unitPrice: 0, vatRate: 19 });
  }
  return {
    rentalNumber: order.number,
    customerNumber: order.customerNumber,
    customerName: order.customerName,
    periodLabel: 'Schlussabrechnung',
    periodFrom: order.startDate ?? null,
    periodTo: order.endDate ?? null,
    lines,
  };
}
